/** @format **/

import React, { FunctionComponent, ReactElement, useState } from 'react';
import GridItem from '../../../components/Grid/GridItem';
import { Dialog, DialogActions, DialogContent, TextareaAutosize, Typography } from '@material-ui/core';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import GridContainer from '../../../components/Grid/GridContainer';
import MvnoBlockRequest from '../../../interfaces/platform/MvnoBlockRequest';
import MvnoBlockRequestState from '../../../interfaces/platform/MvnoBlockRequestState';
import I18n from '../../../components/Translate/I18n';
import { useBlockFunctionList } from '../../../modules/lib/hooks/useBlockFunctionList';
import { SimpleSelect, SimpleSelectElements } from '../../../components/Adescom/SimpleSelect';
import CustomInputExtNumber from '../../../components/CustomInput/CustomInputExtNumber';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { provisionigConstants } from '../../../modules/provisioning/action';
import { operationAction } from '../../../modules/block/OperationStatus';
import { provConstant } from '../../../modules/provisioning/ProvisioningConnect';

type BlockChangeParamsType = {
    open: boolean;
    close(): void;
    mvnoBlockRequest: MvnoBlockRequest;
    mvnoBlockRequestState: MvnoBlockRequestState;
};

export const BlockChangeParams: FunctionComponent<BlockChangeParamsType> = ({
    open,
    close,
    mvnoBlockRequest,
    mvnoBlockRequestState,
}): ReactElement => {
    const dispatch = useDispatch();
    const [containsConditions, setContainsConditions] = useState<string>(mvnoBlockRequestState.params ?? '');
    const [response, setResponse] = useState<string>('');
    const [containsBlockFunctionId, setContainsBlockFunctionId] = useState<number>(mvnoBlockRequestState.mvnoBlockFunctionId);
    const [containsSleepSeconds, setContainsSleepSeconds] = useState<number>(mvnoBlockRequestState.sleepSeconds);
    const functionList = useBlockFunctionList();

    const functionElements: Array<SimpleSelectElements> = [];
    if (functionList && functionList['hydra:member']) {
        for (let i = 0; i < functionList['hydra:member']?.length; i++) {
            functionElements.push({
                name: functionList['hydra:member'][i].displayName,
                value: functionList['hydra:member'][i].id,
            });
        }
    }

    const savePosition = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_blocks/' + mvnoBlockRequest.id),
            provisionigConstants.OPERTATION_ON_BLOCK,
            {
                id: mvnoBlockRequest.id,
                mvnoBlockRequestStateId: mvnoBlockRequestState.id,
                operationType: operationAction.OPERATION_CHANGE_POSITION_PARAMS,
                mvnoBlockRequestStateParams: JSON.stringify({
                    sleepSeconds: containsSleepSeconds,
                    messageResponse: response,
                    params: containsConditions,
                    mvnoBlockFunctionId: containsBlockFunctionId,
                }),
            },
            (data: any) => {
                dispatch({
                    type: provConstant.MVNO_BLOCK_VIEW_RELOAD,
                    reload: true,
                });
                close();
            },
            { propagateDataToCallback: true }
        );
    };

    const onChangeEventTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        switch (field) {
            case 'params':
                setContainsConditions(value);
                break;
            case 'responseMessage':
                setResponse(value);
                break;
            default:
                return;
        }
        setContainsConditions(value);
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = event.target.value;
        setContainsBlockFunctionId(parseInt(selected));
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        switch (field) {
            case 'sleepSeconds':
                setContainsSleepSeconds(parseInt(value));
                break;
            default:
                return;
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <Typography variant="subtitle2">
                            <I18n trans="prov:block.edit_position_on_block" />
                        </Typography>
                        <hr />
                    </GridItem>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:block.choose_block_function"
                            required={true}
                            fieldName="mvnoBlockVunctionId"
                            elements={functionElements}
                            dispatchName={'no-dispatch'}
                            callback={onChangeSelectEvent}
                            selectedValue={containsBlockFunctionId}
                        />
                    </GridItem>
                    <GridItem xs={12} md={5} lg={5}>
                        <CustomInputExtNumber
                            formControlProps={{ fullWidth: true }}
                            required={false}
                            labelText="prov:block.sleep_time_before_execute"
                            name="sleepSeconds"
                            value={containsSleepSeconds}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12} style={{ marginTop: '20px' }}>
                        <Typography variant="body2">
                            <I18n trans="prov:block.set_request_message" />
                        </Typography>
                        <TextareaAutosize
                            id="for-description"
                            style={{ width: '100%', resize: 'none' }}
                            name="params"
                            rowsMin={8}
                            rowsMax={8}
                            onChange={onChangeEventTextArea}
                            defaultValue={containsConditions}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <Typography variant="body2">
                            <I18n trans="prov:block.set_response_message" />
                        </Typography>
                        <TextareaAutosize
                            id="for-response-description"
                            style={{ width: '100%', resize: 'none' }}
                            name="responseMessage"
                            rowsMin={8}
                            rowsMax={8}
                            onChange={onChangeEventTextArea}
                            defaultValue={response}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={savePosition} callbackParam={{}} label="prov:block.save_position" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
